import { create } from 'zustand';

import { getScraperHealth } from '@/entities/scraper';
import { workspaceQueryOptions } from '@/entities/workspace';
import { getSourceByScraperNameSafe } from '@/features/Scraping/configs/config-actions';
import type { IntegrationItem } from '@/features/Setting/Integrations/types';
import { INTEGRATION_STATUS } from '@/features/Setting/Integrations/types';
import service from '@/services/index';
import { queryClient } from '@/shared/lib/query-client';
import { isScrapingSourceType, type SOURCE_TYPE } from '@/types/feedbackConst';

type State = {
  sourceInfo: IntegrationItem[] | null;
};

type Actions = {
  setState: StoreSetter<State>;
  fetchFeedbackSources: (
    workspaceAlias: string,
    isPublic?: boolean,
  ) => Promise<IntegrationItem[]>;
  updateSourceInfo: (sourceInfo: IntegrationItem) => void;
  initFeedbackSource: () => void;
};

const initialState: State = {
  sourceInfo: null,
};

type ScraperHealthItem = Awaited<
  ReturnType<typeof getScraperHealth>
>['data'][number];

const integrationStatusPriority: Record<INTEGRATION_STATUS, number> = {
  [INTEGRATION_STATUS.HEALTHY]: 0,
  [INTEGRATION_STATUS.WARNING]: 1,
  [INTEGRATION_STATUS.UNHEALTHY]: 2,
  [INTEGRATION_STATUS.DISABLE]: -1,
};

const scraperHealthIntegrationStatusMap: Record<
  ScraperHealthItem['status'],
  INTEGRATION_STATUS
> = {
  HEALTHY: INTEGRATION_STATUS.HEALTHY,
  WARNING: INTEGRATION_STATUS.WARNING,
  UNHEALTHY: INTEGRATION_STATUS.UNHEALTHY,
};

const getScrapingHealthStatusMap = (items: ScraperHealthItem[]) => {
  const statusMap = new Map<SOURCE_TYPE, INTEGRATION_STATUS>();

  items.forEach((item) => {
    const source = getSourceByScraperNameSafe(item.scraper_name);

    if (!source) {
      return;
    }

    const nextStatus = scraperHealthIntegrationStatusMap[item.status];
    const currentStatus = statusMap.get(source);

    if (
      !currentStatus ||
      integrationStatusPriority[nextStatus] >
        integrationStatusPriority[currentStatus]
    ) {
      statusMap.set(source, nextStatus);
    }
  });

  return statusMap;
};

const buildScrapingIntegrationItemsFromScraperNames = (
  scraperNames: string[],
): IntegrationItem[] => {
  const sourceSet = new Set<SOURCE_TYPE>();

  scraperNames.forEach((scraperName) => {
    const source = getSourceByScraperNameSafe(scraperName);

    if (!source || !isScrapingSourceType(source)) {
      return;
    }

    sourceSet.add(source);
  });

  return Array.from(sourceSet, (source) => ({
    source,
    status: INTEGRATION_STATUS.HEALTHY,
  }));
};

const mergeScrapingHealthStatus = (
  sourceInfo: IntegrationItem[],
  scraperHealthItems: ScraperHealthItem[],
) => {
  const scrapingHealthStatusMap =
    getScrapingHealthStatusMap(scraperHealthItems);

  return sourceInfo.map((item) => {
    if (!isScrapingSourceType(item.source)) {
      return item;
    }

    if (item.status === INTEGRATION_STATUS.DISABLE) {
      return item;
    }

    return {
      ...item,
      status:
        scrapingHealthStatusMap.get(item.source) ?? INTEGRATION_STATUS.HEALTHY,
    };
  });
};

const mergeIntegrationSourceItems = (sourceInfo: IntegrationItem[]) => {
  const mergedSources = new Map<SOURCE_TYPE, IntegrationItem>();

  sourceInfo.forEach((item) => {
    const current = mergedSources.get(item.source);

    if (!current) {
      mergedSources.set(item.source, item);
      return;
    }

    mergedSources.set(item.source, {
      ...current,
      ...item,
    });
  });

  return Array.from(mergedSources.values());
};

const mergeFeedbackSources = ({
  integrations,
  scraperNames,
  scraperHealthItems,
}: {
  integrations: IntegrationItem[];
  scraperNames: string[];
  scraperHealthItems: ScraperHealthItem[] | null;
}) => {
  const scrapingSources = scraperHealthItems
    ? mergeScrapingHealthStatus(
        buildScrapingIntegrationItemsFromScraperNames(scraperNames),
        scraperHealthItems,
      )
    : buildScrapingIntegrationItemsFromScraperNames(scraperNames);

  return mergeIntegrationSourceItems([...integrations, ...scrapingSources]);
};

const buildPublicFeedbackSources = (integrations: IntegrationItem[]) => {
  return mergeIntegrationSourceItems(integrations);
};

const useFeedbackSourceStore = create<State & Actions>((set, get) => ({
  ...initialState,
  setState: set,
  fetchFeedbackSources: async (workspaceAlias, isPublic) => {
    const integrations = await service.integration.fetchIntegrationInfo(
      workspaceAlias,
      isPublic,
    );

    const data = isPublic
      ? buildPublicFeedbackSources(integrations)
      : await (async () => {
          const [scraperHealthResponse, workspace] = await Promise.all([
            getScraperHealth().catch(() => null),
            queryClient.ensureQueryData(
              workspaceQueryOptions({
                workspaceId: workspaceAlias,
              }),
            ),
          ]);

          const scraperNames = workspace.config?.scraper_names ?? [];
          return mergeFeedbackSources({
            integrations,
            scraperNames,
            scraperHealthItems: scraperHealthResponse?.data ?? null,
          });
        })();

    set({
      sourceInfo: data,
    });
    return data;
  },
  updateSourceInfo: (sourceInfo: IntegrationItem) => {
    const newSourceInfo = [...(get().sourceInfo || [])];
    newSourceInfo.upsert(
      sourceInfo,
      (item) => item.source === sourceInfo.source,
    );
    set({ sourceInfo: newSourceInfo });
  },
  initFeedbackSource: () => {
    set(initialState);
  },
}));

export default useFeedbackSourceStore;
